import { Trophy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Team } from '@/types/game';
import { getTeamColors } from '@/utils/game-utils';

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy className="w-6 h-6 text-neon-yellow" />;
    case 2:
      return <Trophy className="w-5 h-5 text-gray-400" />;
    case 3:
      return <Trophy className="w-5 h-5 text-orange-400" />;
    default:
      return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-muted-foreground">#{rank}</span>;
  }
};

interface TeamRankingsProps {
  teams: Team[];
}

export const TeamRankings = ({ teams }: TeamRankingsProps) => {
  if (teams.length <= 1) return null;

  return (
    <Card className="p-3 sm:p-6 border-2 border-primary/20 bg-card/50 backdrop-blur-sm animate-slide-up">
      <h2 className="text-lg sm:text-2xl font-bold mb-4 sm:mb-6 text-center flex items-center justify-center space-x-2">
        <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-neon-yellow" />
        <span>Team Rankings</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {teams.map((team, index) => {
          const colors = getTeamColors(team.team);
          return (
            <Card
              key={team.team}
              className={`p-3 sm:p-4 border-2 ${colors.border} ${colors.bg} backdrop-blur-sm transition-all duration-300 hover:${colors.glow} animate-float`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="text-center space-y-1 sm:space-y-2">
                <div className="flex items-center justify-center space-x-2">
                  {getRankIcon(parseInt(team.rank))}
                  <span className={`text-base sm:text-xl font-bold ${colors.text}`}>
                    Team {team.team}
                  </span>
                </div>
                <div className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>
                  {parseInt(team.score).toLocaleString()}
                </div>
                <Badge variant="secondary" className="text-xs">
                  Platz {team.rank}
                </Badge>
              </div>
            </Card>
          );
        })}
      </div>
    </Card>
  );
};
